import { createSlice } from '@reduxjs/toolkit';
import { createWorkFlow, DeleteProcessName, fetchWorkflows, getFieldList, getWorkFlow, listWorkflowRoles, reOrderWorkFLow, updateWorkFlow, saveGlobalWorkflowSettings, saveEscalationSettings } from './asyncThunkAPI';
import { InitialState } from './initialState';
import { checkForValidations, workflowDataManager } from './DataProcessor';
import i18n from '../../../utilities/i18n';

const workflowManagementSlice = createSlice({
    name: 'workflow',
    initialState: InitialState,
    reducers: {
        addRow: (state) => {
            if (state.is_new_row_present) {
                state.notitify_message.message = i18n.t('work_flow.common_message.actions_already_in_progress');
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
                return;
            }

            // 1. Mutate items directly in the Immer draft instead of reassigning state.workFlow = ...
            state.workFlow.forEach(item => {
                if (item.isEditing) {
                    item.isEditing = false;
                }
            });

            // 2. Generate and push the new row
            const workFlowData = workflowDataManager({}, state.workFlow, 'new', 'pre_billing');
            state.workFlow.push(workFlowData);

            state.is_new_row_present = true;
        },
        addARWorkFlow: (state) => {
            if (!state.ArWorkflow) {
                state.ArWorkflow = [];
            }
            if (state.ArWorkflow.length === 0) {
                const workFlowData = workflowDataManager({}, state.ArWorkflow, 'new', 'post_billing');
                state.ArWorkflow.push(workFlowData);
            }
        },
        updateField: (state, { payload: { id, field, value, index, type = "pre_billing" } }) => {
                const targetList = type === "isAR" ? state.ArWorkflow : state.workFlow;
                const row = id !== undefined
                    ? targetList.find(r => r.id === id)
                    : targetList[index];
                if (row) {
                    row[field] = value;
                }
        },
        toggleEditMode: (state, action) => {
            const row = state.workFlow.find(r => r.id === action.payload);
            if (row) {
                row.isEditing = !row.isEditing;
                if (!row.isEditing) row.isNew = false;
            }
        },
        deleteRow: (state, action) => {
            let { id, process_name, show, index } = action.payload
            if (!id) {
                state.workFlow.splice(index, 1);
                state.validations.splice(index, 1)
                state.is_new_row_present = false;
                return
            }
            state.deleteModalDetails.id = id;
            state.deleteModalDetails.process_name = process_name;
            state.deleteModalDetails.show_modal = show;
        },
        clearRow: (state, action) => {
            const index = state.workFlow.findIndex(r => r.id === action.payload);
            if (index !== -1) {
                state.workFlow[index] = {
                    ...state.workFlow[index],
                    process_name: '',
                    field_group_ids: [],
                    role_ids: []
                };
            }
        },
        reorderRows: (state, action) => {
            const { dragIndex, hoverIndex } = action.payload;
            const newRows = [...state.workFlow];
            const [removed] = newRows.splice(dragIndex, 1);
            newRows.splice(hoverIndex, 0, removed);
            state.workFlow = newRows;
        },
        setGlobalToggle: (state, action) => {
            state.toggleSwitch = action.payload;
        },
        showHideNotification: (state, action) => {
            let { show, message = "", type = "" } = action.payload;
            state.notitify_message.show = show;
            state.notitify_message.message = message;
            state.notitify_message.type = type;
            state.deleteModalDetails.id = "";
            state.deleteModalDetails.process_name = "";
            state.deleteModalDetails.show_modal = false
        },
        reverseEntry: (state, action) => {
            let {index,type}=action.payload;
            const targetList = type === "isAR" ? state.ArWorkflow : state.workFlow;
            let reverseData = state.duplicate;
            reverseData.isEditing = false;
            targetList[index] = reverseData;
            state.duplicate = {};
        },
        closeModal: (state) => {
            state.deleteModalDetails.id = "";
            state.deleteModalDetails.process_name = "";
            state.deleteModalDetails.show_modal = false;
        },
        setEnableAuditing: (state, actions) => {
            state.enableAuditing = actions.payload;
        },
        setEscalationSettings: (state, action) => {
            const { field, value, type } = action.payload || {};

            // Prevent execution if field is missing or invalid
            if (!field) return;

            // Ensure escalationSettings exists on state before spreading
            const currentSettings = state.escalationSettings || {};

            if (type === "switch") {
                state.escalationSettings = {
                    ...currentSettings,
                    [field]: Boolean(value)
                };
            } else {
                // Safe check for arrays, strings, or other types without throwing continuous errors
                const hasValue = Array.isArray(value) || typeof value === "string"
                    ? value.length > 0
                    : Boolean(value);

                if (hasValue) {
                    state.escalationSettings = {
                        ...currentSettings,
                        [field]: value
                    };
                } else {
                    state.escalationSettings = {
                        ...currentSettings,
                        [field]: []
                    };
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Workflows
            .addCase(fetchWorkflows.pending, (state) => {
                state.loading = true;
                state.worflow = [];
                state.ArWorkflow=[];
            })


            .addCase(fetchWorkflows.fulfilled, (state, action) => {
                state.loading = false;
                const workFlowData = action?.payload || [];
                if (workFlowData.length === 0) {
                    state.workFlow = [];
                    state.toggleSwitch = false;
                } else {
                    const responseData = workFlowData[0];
                    const {
                        workflows = [],
                        follow_sequential_workflow = false,
                        has_assigned_claims = false,
                        enable_auditing_workflow = false,
                        view_client_escalation_roles = [],
                        initiate_client_escalation_roles = []
                    } = responseData;

                    if (responseData) {
                        state.toggleSwitch = !follow_sequential_workflow;
                        state.has_assigned_workflow = has_assigned_claims;
                        const workflowData = workflows.length > 0 ? workflows : [];
                        state.is_new_row_present = false;
                        state.escalationSettings = {
                            view_client_escalation_roles: view_client_escalation_roles ?? [],
                            initiate_client_escalation_roles: initiate_client_escalation_roles ?? [],
                        }
                        state.escalationSettingsDuplicate = {
                            view_client_escalation_roles: view_client_escalation_roles ?? [],
                            initiate_client_escalation_roles: initiate_client_escalation_roles ?? [],
                        }
                        state.enableAuditing = enable_auditing_workflow;
                        state.workFlow = workflowData
                            .filter(item => item.stage === 'pre_billing')
                            .map(item => workflowDataManager(item, [], 'edit', 'pre_billing'));
                        
                        const arItems = workflowData.filter(item => item.stage === 'ar_denial_management'); // Replace with your actual field/value

                        if (arItems.length > 0) {
                            state.ArWorkflow = arItems.map(item =>
                                workflowDataManager(item, [], 'edit', 'post_billing')
                            );
                        } else {
                            state.ArWorkflow = [];
                        }

                    } else {
                        state.workFlow = [];
                        state.ArWorkflow = [];
                        state.toggleSwitch = false;
                        state.has_assigned_workflow = false;
                        state.escalationSettings = InitialState.escalationSettings;
                    }
                }
            })

            .addCase(fetchWorkflows.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.worflow = [];
                state.ArWorkflow=[];
            })
            // createWorkFlow
            .addCase(createWorkFlow.pending, (state) => {
                state.loading = true;
            })
            .addCase(createWorkFlow.fulfilled, (state, action) => {
                state.loading = false;
                let { data, index, id } = action.payload;
                state.workFlow[index] = { ...data, isEditing: false, isNew: false, id: id };
                state.notitify_message.message = i18n.t('work_flow.common_message.creation_sucess');
                state.notitify_message.type = "success";
                state.notitify_message.show = true;
                state.is_new_row_present = false;

                state.workFlow = state.workFlow.map(item => {
                    if (item.isEditing === true) {
                        return { ...item, isEditing: false };
                    }
                    return item;
                });
            })
            .addCase(createWorkFlow.rejected, (state, action) => {
                let { message, index, errors } = action.payload;
                state.loading = false;
                state.error = message;
                if (index !== "") {
                    state.validations[index] = { ...state.validations[index], ...errors }
                }
                state.notitify_message.message = message;
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
            })
            .addCase(updateWorkFlow.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateWorkFlow.fulfilled, (state, action) => {
                state.loading = false;
                let { data, index, id,stage } = action.payload;
                const targetKey = stage === "ar_denial_management" ? "ArWorkflow" : "workFlow";
                state[targetKey][index] = { ...data, id: id, isEditing: false, isNew: false };
                state.notitify_message.message = i18n.t('work_flow.common_message.update_sucess');
                state.notitify_message.type = "success";
                state.notitify_message.show = true;
            })
            .addCase(updateWorkFlow.rejected, (state, action) => {
                let { message, index, errors } = action.payload;
                state.loading = false;
                state.error = message;
                if (index !== "") {
                    state.validations[index] = { ...state.validations[index], ...errors }
                }
                state.notitify_message.message = message;
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
                state.is_new_row_present = false;
            })
            .addCase(getFieldList.pending, (state) => {
                state.loading = true;
            })
            .addCase(getFieldList.fulfilled, (state, action) => {
                state.loading = false;
                state.fieldList = action.payload
            })
            .addCase(getFieldList.rejected, (state) => {
                state.loading = false;
            })
            .addCase(listWorkflowRoles.pending, (state) => {
                state.loading = true;
            })
            .addCase(listWorkflowRoles.fulfilled, (state, action) => {
                state.loading = false;
                state.roles = action.payload
            })
            .addCase(listWorkflowRoles.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getWorkFlow.pending, (state) => {
                state.loading = true;
            })
            .addCase(getWorkFlow.fulfilled, (state, action) => {
                const { data, type, id } = action.payload;
                state.loading = false;
            
                // 1. Determine target array dynamically
                const targetKey = type === "post_billing" ? "ArWorkflow" : "workFlow";
                const workFlowArray = state[targetKey];
            
                // 2. Find the target row index
                const rowIndex = workFlowArray.findIndex(r => r.id === id);
            
                if (rowIndex !== -1) {
                    // 3. Prevent editing if another row is active
                    const isAnyRowEditing = workFlowArray.some((row, index) => index !== rowIndex && row.isEditing);
            
                    if (isAnyRowEditing) {
                        state.notitify_message = {
                            message: i18n.t('work_flow.common_message.edit_actions_already_in_progress'),
                            type: "error",
                            show: true
                        };
                        return;
                    }
            
                    const existingRow = workFlowArray[rowIndex];
            
                    // 4. Create updated row (preserving existing properties)
                    const updatedRow = {
                        ...existingRow,
                        id,
                        process_name: data?.process_name,
                        sequence_num: data?.sequence_num,
                        field_group_ids: Array.isArray(data?.field_groups)
                            ? data.field_groups.map(g => g?.id).filter(Boolean)
                            : [],
                        role_ids: Array.isArray(data?.roles)
                            ? data.roles.map(r => r?.id).filter(Boolean)
                            : [],
                        stage:data?.stage,
                        isEditing: !existingRow.isEditing,
                    };
            
                    if (!updatedRow.isEditing) {
                        updatedRow.isNew = false;
                    }
            
                    // 5. Update the CORRECT array
                    workFlowArray[rowIndex] = updatedRow;
            
                    // 6. Update duplicates and validations
                    state.duplicate = updatedRow;
                    const validation = checkForValidations(updatedRow);
                    state.validations[rowIndex] = { 
                        ...state.validations[rowIndex], 
                        ...validation.errors 
                    };
                }
            })
            .addCase(getWorkFlow.rejected, (state) => {
                state.loading = false;
            })

            .addCase(DeleteProcessName.pending, (state) => {
                state.loading = true;
            })
            .addCase(DeleteProcessName.fulfilled, (state, action) => {
                state.loading = false;
                state.notitify_message.message = i18n.t('work_flow.common_message.delete_success');
                state.notitify_message.type = "success";
                state.notitify_message.show = true;

                if (state.workFlow.length <= 1) {
                    state.workFlow = [];
                    return
                }

                state.validations = [];

                const itemToDelete = state.workFlow.find(item => item.id === action.payload);
                if (!itemToDelete) return state.workFlow;

                const deletedSeq = itemToDelete.sequence_num;
                state.workFlow = state.workFlow.filter(item => item.id !== action.payload);
                state.workFlow.forEach(item => {
                    if (item.sequence_num > deletedSeq) {
                        item.sequence_num -= 1;
                    }
                });
            })
            .addCase(DeleteProcessName.rejected, (state, action) => {
                state.loading = false;
                state.notitify_message.message = action.payload;
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
                state.deleteModalDetails.id = "";
                state.deleteModalDetails.process_name = "";
                state.deleteModalDetails.show_modal = false;
            })
            .addCase(reOrderWorkFLow.pending, (state) => {
                state.loading = true;
            })
            .addCase(reOrderWorkFLow.fulfilled, (state, action) => {
                state.loading = false;
                let { hoverIndex, dragIndex } = action.payload;
                const newRows = [...state.workFlow];
                const [removed] = newRows.splice(dragIndex, 1);
                newRows.splice(hoverIndex, 0, removed);
                state.workFlow = newRows.map((row, index) => ({
                    ...row,
                    sequence_num: index + 1,
                    isEditing: false,
                    isNew: false
                }));
            })
            .addCase(reOrderWorkFLow.rejected, (state) => {
                state.loading = false;
            })

            // saveGlobalWorkflowSettings
            .addCase(saveGlobalWorkflowSettings.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveGlobalWorkflowSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.toggleSwitch = action.payload.allowMultipleWorkflows;
                state.notitify_message.message = i18n.t('work_flow.common_message.update_sucess');
                state.notitify_message.type = "success";
                state.notitify_message.show = true;
            })
            .addCase(saveGlobalWorkflowSettings.rejected, (state, action) => {
                state.loading = false;
                state.notitify_message.message = action.payload || i18n.t('work_flow.common_message.update_error');
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
            })
            .addCase(saveEscalationSettings.pending, (state) => {
                state.loading = true;
            })
            .addCase(saveEscalationSettings.fulfilled, (state) => {
                state.loading = false;
                state.notitify_message.message = i18n.t('work_flow.common_message.escalation_settings_update_success');
                state.notitify_message.type = "success";
                state.notitify_message.show = true;
            })
            .addCase(saveEscalationSettings.rejected, (state, action) => {
                state.loading = false;
                state.notitify_message.message = action.payload || i18n.t('work_flow.api_error.escalation_settings_update_failed');
                state.notitify_message.type = "error";
                state.notitify_message.show = true;
            })
    }
});

export const {
    addRow, deleteRow, reorderRows, clearRow, toggleEditMode, updateField, setGlobalToggle,
    showHideNotification, reverseEntry, closeModal, setEnableAuditing, setEscalationSettings, addARWorkFlow
} = workflowManagementSlice.actions;

export default workflowManagementSlice.reducer;